import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'

import {
    ArrowUpRight,
    Facebook,
    Instagram,
    Mail,
    MapPin,
    Phone
} from 'lucide-react'
import { safely } from '@/lib/db'
import { getServices } from '@/lib/content'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { WhatsAppIcon } from '@/components/generals/icons'
import { InquiryForm } from '@/components/forms/inquiry-form'
import { CDN_BASE, contactInfo, eventRegions, whatsappLink } from '@/constants/site'

type ServiceOption = {
    id: string
    slug: string
    title: string
}

interface ContactProps {
    services: ServiceOption[]
}

const Contact: FC<ContactProps> = ({ services }): ReactNode => {
    const channels = [
        {
            icon: Phone,
            label: 'Call us',
            value: contactInfo.phoneDisplay,
            href: contactInfo.phoneHref,
            external: false
        },
        {
            icon: WhatsAppIcon,
            label: 'WhatsApp',
            value: 'Chat with our team',
            href: whatsappLink('Hi Guju Events, I would like to plan an event.'),
            external: true
        },
        {
            icon: Mail,
            label: 'Email',
            value: contactInfo.email,
            href: `mailto:${contactInfo.email}`,
            external: false
        },
        {
            icon: MapPin,
            label: 'Visit',
            value: contactInfo.addressFull,
            href: contactInfo.mapsUrl,
            external: true
        }
    ]

    return (
        <Layout>
            <Seo
                title='Contact'
                description='Plan your wedding, corporate launch, Navratri night, concert or manpower requirement with Guju Events. Call +91 90339 79998 or send an inquiry.'
            />

            <PageHero
                eyebrow='Contact us'
                title={(
                    <>
                        Let&apos;s create
                        {' '}
                        <em className='gold-gradient-text pr-2'>magic</em>
                    </>
                )}
                description='Reach out for weddings, corporate launches, sports events or manpower services. Share a few details and we will take it from there.'
                image={`${CDN_BASE}/images/gallery/wedding-event-5.png`}
            />

            <section id='inquiry' aria-labelledby='inquiry-title' className='scroll-mt-20 bg-ivory py-20 lg:py-28'>
                <div className='mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-12 lg:gap-14'>
                    <div className='lg:col-span-5'>
                        <p className='inline-flex items-center gap-3 text-xs font-bold tracking-[0.24em] text-crimson uppercase'>
                            <span aria-hidden='true' className='h-px w-8 bg-crimson/50' />
                            Get in touch
                        </p>

                        <h2 className='mt-4 font-heading text-4xl leading-[1.05] tracking-tight text-ink sm:text-5xl'>
                            Talk to the team that
                            {' '}
                            <em className='text-crimson'>runs the day</em>
                        </h2>

                        <p className='mt-5 text-base leading-relaxed text-muted-foreground'>
                            Whether you are planning a wedding, a brand launch or need trained staff for a stadium, the quickest way to start is a call or a WhatsApp message.
                        </p>

                        <ul className='mt-10 space-y-3'>
                            {channels.map(({ icon: Icon, label, value, href, external }) => (
                                <li key={label}>
                                    <a
                                        href={href}
                                        target={external ? '_blank' : undefined}
                                        rel={external ? 'noopener noreferrer' : undefined}
                                        className='focus-ring group flex items-center gap-4 rounded-2xl border border-ink/8 bg-white p-4 transition-all hover:border-crimson/30 hover:shadow-[0_20px_40px_-30px_rgba(27,18,25,0.5)]'
                                    >
                                        <span className='inline-flex size-12 shrink-0 items-center justify-center rounded-xl bg-night text-gold transition-colors group-hover:bg-crimson group-hover:text-white'>
                                            <Icon className='size-5' aria-hidden='true' />
                                        </span>

                                        <span className='min-w-0 flex-1'>
                                            <span className='block text-xs font-bold tracking-[0.18em] text-muted-foreground uppercase'>
                                                {label}
                                            </span>
                                            <span className='mt-0.5 block font-semibold break-words text-ink'>
                                                {value}
                                            </span>
                                        </span>

                                        <ArrowUpRight className='size-5 shrink-0 text-ink/30 transition-colors group-hover:text-crimson' aria-hidden='true' />
                                    </a>
                                </li>
                            ))}
                        </ul>

                        <div className='mt-10 rounded-3xl bg-night p-7 text-white'>
                            <p className='text-xs font-bold tracking-[0.22em] text-gold uppercase'>
                                Our major events
                            </p>

                            <ul className='mt-4 flex flex-wrap gap-2'>
                                {eventRegions.map((region) => (
                                    <li key={region} className='rounded-full border border-white/15 px-3 py-1.5 text-sm text-white/85'>
                                        {region}
                                    </li>
                                ))}
                            </ul>

                            <div className='mt-6 flex items-center gap-3 border-t border-white/10 pt-6'>
                                <span className='text-sm text-white/60'>
                                    Follow our work
                                </span>

                                <a href={contactInfo.instagram} target='_blank' rel='noopener noreferrer' aria-label='Guju Events on Instagram' className='focus-ring inline-flex size-10 items-center justify-center rounded-full border border-white/15 transition-colors hover:border-gold hover:text-gold'>
                                    <Instagram className='size-4' aria-hidden='true' />
                                </a>

                                <a href={contactInfo.facebook} target='_blank' rel='noopener noreferrer' aria-label='Guju Events on Facebook' className='focus-ring inline-flex size-10 items-center justify-center rounded-full border border-white/15 transition-colors hover:border-gold hover:text-gold'>
                                    <Facebook className='size-4' aria-hidden='true' />
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className='lg:col-span-7'>
                        <div className='rounded-[2rem] border border-ink/8 bg-white p-6 shadow-[0_40px_80px_-50px_rgba(27,18,25,0.5)] sm:p-10'>
                            <h2 id='inquiry-title' className='font-heading text-3xl text-ink sm:text-4xl'>
                                Quick inquiry
                            </h2>

                            <p className='mt-2 mb-8 text-sm text-muted-foreground'>
                                Tell us a little about your event. It takes under a minute.
                            </p>

                            <InquiryForm services={services} />
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<ContactProps> = async () => {
    const services = await safely('contact services', () => getServices(), [])

    return {
        props: {
            services: services.map(({ id, slug, title }) => ({ id, slug, title }))
        },
        revalidate: 60
    }
}

export default Contact
